import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
	TDownloadInvoicePdfRequest,
	TDownloadInvoicePdfResponsePayload,
	TDownloadInvoiceUdrCsvRequest,
	TDownloadInvoiceUdrCsvResponsePayload,
	TInvoiceManagementStore,
	TInvoiceListRequest,
	TInvoiceListResponsePayload,
	TUpdateInvoiceCommentRequest,
	TUpdateInvoiceCommentResponsePayload,
	TUpdateInvoicePaymentRequest,
	TUpdateInvoicePaymentResponsePayload,
	TSendEmailForInvoiceRequest,
	TSendEmailForInvoiceResponsePayload,
	TUpdateInvoiceGpsrRequest,
	TUpdateInvoiceGpsrResponsePayload,
	TEncryptInvoiceNumberRequest,
	TEncryptInvoiceNumberResponsePayload,
	TNullifyInvoiceRequest,
	TNullifyInvoiceResponsePayload,
	TRemoveInvoiceLpfRequest,
	TRemoveInvoiceLpfResponsePayload,
	TInvoiceListExcelExportRequest,
	TInvoiceListExcelExportResponsePayload,
	TDownloadInvoiceArchiveRequest,
	TCreditNote,
	TCreditNoteIdRequest,
	TCreditNoteListResponse,
	TCreditNoteSearchRequest,
} from './types';

// ==========================================
// Initial State
// ==========================================

const initialState: TInvoiceManagementStore = {
	invoiceList: null,
	invoiceListLoading: false,
	downloadInvoicePdfLoader: false,
	downloadedInvoicePdf: null,
	downloadInvoiceUdrCsvLoader: false,
	downloadedInvoiceUdrCsv: null,
	updateInvoicePaymentLoader: false,
	updateInvoiceCommentLoader: false,
	sendEmailForInvoiceLoader: false,
	updateInvoiceGpsrLoader: false,
	encryptInvoiceNumberLoader: false,
	encryptInvoiceNumber: null,
	nullifyInvoiceLoader: false,
	removeInvoiceLpfLoader: false,
	invoiceListExcelExportLoader: false,
	invoiceListExcelExport: null,
	downloadInvoiceArchiveLoader: false,
	creditNotes: null,
	creditNotesLoading: false,
	creditNoteDetails: null,
	creditNoteDetailsLoading: false,
};

// ==========================================
// Slice
// ==========================================

const invoiceManagementSlice = createSlice({
	name: 'invoiceManagement',
	initialState,
	reducers: {
		// Get Invoice List
		getInvoiceListRequest: (state, _action: PayloadAction<TInvoiceListRequest>) => {
			state.invoiceListLoading = true;
		},
		getInvoiceListSuccess: (state, action: PayloadAction<TInvoiceListResponsePayload>) => {
			state.invoiceList = action.payload;
			state.invoiceListLoading = false;
		},
		getInvoiceListFailure: (state) => {
			state.invoiceListLoading = false;
		},
		// Clear state
		clearInvoiceList: (state) => {
			state.invoiceList = null;
		},
		// Download Invoice PDF
		downloadInvoicePdfRequest: (state, _action: PayloadAction<TDownloadInvoicePdfRequest>) => {
			state.downloadInvoicePdfLoader = true;
		},
		downloadInvoicePdfSuccess: (
			state,
			action: PayloadAction<TDownloadInvoicePdfResponsePayload>,
		) => {
			state.downloadInvoicePdfLoader = false;
			state.downloadedInvoicePdf = action.payload;
		},
		downloadInvoicePdfFailure: (state) => {
			state.downloadInvoicePdfLoader = false;
		},
		// Download Invoice UDR CSV
		downloadInvoiceUdrCsvRequest: (
			state,
			_action: PayloadAction<TDownloadInvoiceUdrCsvRequest>,
		) => {
			state.downloadInvoiceUdrCsvLoader = true;
		},
		downloadInvoiceUdrCsvSuccess: (
			state,
			action: PayloadAction<TDownloadInvoiceUdrCsvResponsePayload>,
		) => {
			state.downloadInvoiceUdrCsvLoader = false;
			state.downloadedInvoiceUdrCsv = action.payload;
		},
		downloadInvoiceUdrCsvFailure: (state) => {
			state.downloadInvoiceUdrCsvLoader = false;
		},
		// Update Invoice Payment
		updateInvoicePaymentRequest: (state, _action: PayloadAction<TUpdateInvoicePaymentRequest>) => {
			state.updateInvoicePaymentLoader = true;
		},
		updateInvoicePaymentSuccess: (
			state,
			_action: PayloadAction<TUpdateInvoicePaymentResponsePayload>,
		) => {
			state.updateInvoicePaymentLoader = false;
		},
		updateInvoicePaymentFailure: (state) => {
			state.updateInvoicePaymentLoader = false;
		},
		// Update Invoice Comment
		updateInvoiceCommentRequest: (state, _action: PayloadAction<TUpdateInvoiceCommentRequest>) => {
			state.updateInvoiceCommentLoader = true;
		},
		updateInvoiceCommentSuccess: (
			state,
			_action: PayloadAction<TUpdateInvoiceCommentResponsePayload>,
		) => {
			state.updateInvoiceCommentLoader = false;
		},
		updateInvoiceCommentFailure: (state) => {
			state.updateInvoiceCommentLoader = false;
		},
		// Send Email For Invoice
		sendEmailForInvoiceRequest: (state, _action: PayloadAction<TSendEmailForInvoiceRequest>) => {
			state.sendEmailForInvoiceLoader = true;
		},
		sendEmailForInvoiceSuccess: (
			state,
			_action: PayloadAction<TSendEmailForInvoiceResponsePayload>,
		) => {
			state.sendEmailForInvoiceLoader = false;
		},
		sendEmailForInvoiceFailure: (state) => {
			state.sendEmailForInvoiceLoader = false;
		},
		// Update Invoice GPSR
		updateInvoiceGpsrRequest: (state, _action: PayloadAction<TUpdateInvoiceGpsrRequest>) => {
			state.updateInvoiceGpsrLoader = true;
		},
		updateInvoiceGpsrSuccess: (
			state,
			_action: PayloadAction<TUpdateInvoiceGpsrResponsePayload>,
		) => {
			state.updateInvoiceGpsrLoader = false;
		},
		updateInvoiceGpsrFailure: (state) => {
			state.updateInvoiceGpsrLoader = false;
		},
		// Encrypt Invoice Number
		encryptInvoiceNumberRequest: (state, _action: PayloadAction<TEncryptInvoiceNumberRequest>) => {
			state.encryptInvoiceNumberLoader = true;
		},
		encryptInvoiceNumberSuccess: (
			state,
			action: PayloadAction<TEncryptInvoiceNumberResponsePayload>,
		) => {
			state.encryptInvoiceNumberLoader = false;
			state.encryptInvoiceNumber = action.payload;
		},
		encryptInvoiceNumberFailure: (state) => {
			state.encryptInvoiceNumberLoader = false;
		},
		// Nullify Invoice
		nullifyInvoiceRequest: (state, _action: PayloadAction<TNullifyInvoiceRequest>) => {
			state.nullifyInvoiceLoader = true;
		},
		nullifyInvoiceSuccess: (state, _action: PayloadAction<TNullifyInvoiceResponsePayload>) => {
			state.nullifyInvoiceLoader = false;
		},
		nullifyInvoiceFailure: (state) => {
			state.nullifyInvoiceLoader = false;
		},
		// Remove Invoice LPF
		removeInvoiceLpfRequest: (state, _action: PayloadAction<TRemoveInvoiceLpfRequest>) => {
			state.removeInvoiceLpfLoader = true;
		},
		removeInvoiceLpfSuccess: (state, _action: PayloadAction<TRemoveInvoiceLpfResponsePayload>) => {
			state.removeInvoiceLpfLoader = false;
		},
		removeInvoiceLpfFailure: (state) => {
			state.removeInvoiceLpfLoader = false;
		},
		// Invoice List Excel Export
		invoiceListExcelExportRequest: (
			state,
			_action: PayloadAction<TInvoiceListExcelExportRequest>,
		) => {
			state.invoiceListExcelExportLoader = true;
		},
		invoiceListExcelExportSuccess: (
			state,
			action: PayloadAction<TInvoiceListExcelExportResponsePayload>,
		) => {
			state.invoiceListExcelExportLoader = false;
			state.invoiceListExcelExport = action.payload;
		},
		invoiceListExcelExportFailure: (state) => {
			state.invoiceListExcelExportLoader = false;
		},
		// Download Invoice Archive
		downloadInvoiceArchiveRequest: (
			state,
			_action: PayloadAction<TDownloadInvoiceArchiveRequest>,
		) => {
			state.downloadInvoiceArchiveLoader = true;
		},
		downloadInvoiceArchiveSuccess: (state) => {
			state.downloadInvoiceArchiveLoader = false;
		},
		downloadInvoiceArchiveFailure: (state) => {
			state.downloadInvoiceArchiveLoader = false;
		},
		// Credit Notes
		searchCreditNotesRequest: (state, _action: PayloadAction<TCreditNoteSearchRequest>) => {
			state.creditNotesLoading = true;
		},
		searchCreditNotesSuccess: (state, action: PayloadAction<TCreditNoteListResponse>) => {
			state.creditNotesLoading = false;
			state.creditNotes = action.payload;
		},
		searchCreditNotesFailure: (state) => {
			state.creditNotesLoading = false;
		},
		getCreditNoteRequest: (state, _action: PayloadAction<TCreditNoteIdRequest>) => {
			state.creditNoteDetailsLoading = true;
		},
		getCreditNoteSuccess: (state, action: PayloadAction<TCreditNote | null>) => {
			state.creditNoteDetailsLoading = false;
			state.creditNoteDetails = action.payload;
		},
		getCreditNoteFailure: (state) => {
			state.creditNoteDetailsLoading = false;
		},
		clearCreditNoteDetails: (state) => {
			state.creditNoteDetails = null;
		},
	},
});

export const {
	getInvoiceListRequest,
	getInvoiceListSuccess,
	getInvoiceListFailure,
	clearInvoiceList,
	downloadInvoicePdfRequest,
	downloadInvoicePdfSuccess,
	downloadInvoicePdfFailure,
	downloadInvoiceUdrCsvRequest,
	downloadInvoiceUdrCsvSuccess,
	downloadInvoiceUdrCsvFailure,
	updateInvoicePaymentRequest,
	updateInvoicePaymentSuccess,
	updateInvoicePaymentFailure,
	updateInvoiceCommentRequest,
	updateInvoiceCommentSuccess,
	updateInvoiceCommentFailure,
	sendEmailForInvoiceRequest,
	sendEmailForInvoiceSuccess,
	sendEmailForInvoiceFailure,
	updateInvoiceGpsrRequest,
	updateInvoiceGpsrSuccess,
	updateInvoiceGpsrFailure,
	encryptInvoiceNumberRequest,
	encryptInvoiceNumberSuccess,
	encryptInvoiceNumberFailure,
	nullifyInvoiceRequest,
	nullifyInvoiceSuccess,
	nullifyInvoiceFailure,
	removeInvoiceLpfRequest,
	removeInvoiceLpfSuccess,
	removeInvoiceLpfFailure,
	invoiceListExcelExportRequest,
	invoiceListExcelExportSuccess,
	invoiceListExcelExportFailure,
	downloadInvoiceArchiveRequest,
	downloadInvoiceArchiveSuccess,
	downloadInvoiceArchiveFailure,
	searchCreditNotesRequest,
	searchCreditNotesSuccess,
	searchCreditNotesFailure,
	getCreditNoteRequest,
	getCreditNoteSuccess,
	getCreditNoteFailure,
	clearCreditNoteDetails,
} = invoiceManagementSlice.actions;

export default invoiceManagementSlice.reducer;
